import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  FileText,
  Save,
  ShieldCheck,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";

import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import { useAuth } from "@/contexts/AuthContext";
import { api, type ApiClient } from "@/lib/api";
import { getAuthToken as getPortalAuthToken } from "@/lib/authToken";
import type { NdaDocument, NdaSignatureReport, NdaStatus } from "@/types";

const PORTAL_ADMIN = "admin@portal.com";
const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";

const ROLE_BADGE: Record<string, string> = {
  admin: "bg-rose-500/15 text-rose-600",
  bl_advisor: "bg-violet-500/10 text-violet-600",
  seller: "bg-amber-500/15 text-amber-600",
  buyer: "bg-primary/10 text-primary",
  unknown: "bg-slate-100 text-slate-600",
};

const STATUS_BADGE: Record<NdaStatus, string> = {
  published: "bg-emerald-500/15 text-emerald-600",
  draft: "bg-amber-500/15 text-amber-600",
  archived: "bg-slate-100 text-slate-600",
};

function roleLabel(role: string): string {
  if (role === "bl_advisor") return "BL Advisor";
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Open an admin-protected PDF in a new tab by fetching it with the auth header.
 *
 *  The token is passed in rather than read from localStorage directly: the
 *  super admin's token lives in sessionStorage under a different key, and
 *  sending the portal one here would 401. */
async function openProtectedPdf(
  path: string,
  toastError: (m: string) => void,
  getToken: () => string | null,
) {
  try {
    const token = getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Could not load the PDF");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toastError(e instanceof Error ? e.message : "Could not open the PDF");
  }
}

export interface AdminUploadNdaPageProps {
  /** Which console's client to talk to. Defaults to the portal's. */
  client?: ApiClient;
  /** Upload / publish / delete. False in the portal console, which is read-only
   *  now that NDA management belongs to the super admin. The API enforces this
   *  independently — hiding the controls is courtesy, not the gate. */
  canManage?: boolean;
  /** Token for the direct fetch() that streams a PDF, which bypasses `client`. */
  getAuthToken?: () => string | null;
  /** The portal-user redirect below is meaningless in the super admin console,
   *  which has no portal user at all. */
  enforcePortalGuard?: boolean;
  /** Hero heading. The admin console calls this page simply "NDA"; the super
   *  admin console, whose sidebar still says "NDA Management", overrides it. */
  title?: string;
}

export default function AdminUploadNdaPage({
  client = api,
  canManage = false,
  getAuthToken = getPortalAuthToken,
  enforcePortalGuard = true,
  title = "NDA",
}: AdminUploadNdaPageProps = {}) {
  const { user } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<"draft" | "publish" | null>(null);

  const [docs, setDocs] = useState<NdaDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [reportFor, setReportFor] = useState<NdaDocument | null>(null);

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      setDocs(await client.get<NdaDocument[]>("/admin/nda"));
    } catch {
      setDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  }, [client]);

  useEffect(() => {
    void loadDocs();
  }, [loadDocs]);

  // Only the portal admin may reach the portal copy of this page.
  if (enforcePortalGuard && user && user.email !== PORTAL_ADMIN) {
    return <Navigate to="/admin/home" replace />;
  }

  const pickFile = (selected: File | null) => {
    if (!selected) return;
    if (selected.type !== "application/pdf" && !selected.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file");
      return;
    }
    setFile(selected);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async (publish: boolean) => {
    if (!file) {
      toast.error("Choose a PDF first");
      return;
    }
    setBusy(publish ? "publish" : "draft");
    try {
      await client.upload<NdaDocument>("/admin/nda/upload", file, "file", { publish: String(publish) });
      clearFile();
      toast.success(
        publish ? "New NDA published — all users will be asked to sign it" : "NDA saved as a draft"
      );
      await loadDocs();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const publishDraft = async (doc: NdaDocument) => {
    if (!window.confirm(`Publish this NDA? Every user will be required to sign ${doc.version_label}.`)) return;
    try {
      await client.post(`/admin/nda/${doc.id}/publish`, {});
      toast.success("NDA published — all users will be asked to sign it");
      await loadDocs();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Publish failed");
    }
  };

  const deleteDraft = async (doc: NdaDocument) => {
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await client.delete(`/admin/nda/${doc.id}`);
      toast.success("Draft deleted");
      await loadDocs();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Delete failed");
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="Tools"
        title={title}
        description={
          canManage
            ? "Upload a Non-Disclosure Agreement as a draft, or publish it. Publishing a new NDA forces every user to review and sign the latest version before continuing."
            : "Review the platform NDA and see who has signed each version. Uploading and publishing moved to the super admin console."
        }
        aside={
          <div>
            <h2 className="text-lg font-bold text-foreground">How it works</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {canManage && (
                <>
                  <li className="flex gap-2"><Save className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Save a draft to stage an NDA without affecting users. Publish any draft whenever you're ready.</li>
                  <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Publishing makes the PDF active and assigns the next version (v1, v2, v3 …).</li>
                </>
              )}
              <li className="flex gap-2"><Users className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Track who has signed each version — buyers, BL advisors and admins.</li>
            </ul>
          </div>
        }
      />

      {canManage && (
      <PagePanel>
        <PanelHeading title="Upload an NDA" description="Save it as a draft, or publish it to all users." />

        <div className="space-y-5">
          <label
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border/70 bg-muted/30 px-6 py-10 text-center transition-colors hover:bg-muted/50"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFile(e.dataTransfer.files?.[0] ?? null);
            }}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {file ? file.name : "Click to choose a PDF, or drag and drop"}
            </span>
            <span className="text-xs text-muted-foreground">PDF only · max 15 MB</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
          </label>

          <div className="flex flex-wrap items-center justify-end gap-3">
            {file && (
              <button
                type="button"
                onClick={clearFile}
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
            <Button
              variant="outline"
              onClick={() => handleUpload(false)}
              disabled={!file || busy !== null}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {busy === "draft" ? "Saving…" : "Save as draft"}
            </Button>
            <Button onClick={() => handleUpload(true)} disabled={!file || busy !== null} className="gap-2">
              <Upload className="h-4 w-4" />
              {busy === "publish" ? "Publishing…" : "Publish & notify users"}
            </Button>
          </div>
        </div>
      </PagePanel>
      )}

      <PagePanel>
        <PanelHeading title="NDA history" description="Every draft and published version, newest first." />

        {loadingDocs ? (
          <Loader className="min-h-[20vh]" />
        ) : docs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60">
              <FileText className="h-7 w-7 text-muted-foreground/40" />
            </div>
            <p className="mt-4 font-semibold text-foreground">No NDAs yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {canManage ? "Upload a PDF above to get started." : "The super admin has not published an NDA yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border/70 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2.5">Version</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">File</th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Signers</th>
                  {canManage && <th className="px-3 py-2.5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {docs.map((doc) => (
                  <tr key={doc.id} className="align-middle">
                    <td className="px-3 py-3 font-bold text-foreground">
                      {doc.version_label}
                      {doc.is_active && (
                        <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-emerald-600">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${STATUS_BADGE[doc.status]}`}
                      >
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      <button
                        type="button"
                        onClick={() => void openProtectedPdf(`/admin/nda/${doc.id}/pdf`, toast.error, getAuthToken)}
                        className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
                      >
                        <FileText className="h-4 w-4" />
                        {doc.filename ?? "View PDF"}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {formatDate(doc.published_at ?? doc.created_at)}
                    </td>
                    <td className="px-3 py-3">
                      {doc.status === "draft" ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setReportFor(doc)}
                          className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
                        >
                          <Users className="h-4 w-4" />
                          {doc.signature_count}
                        </button>
                      )}
                    </td>
                    {canManage && (
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {doc.status === "draft" && (
                          <>
                            <Button size="sm" onClick={() => publishDraft(doc)} className="gap-1.5">
                              <Upload className="h-3.5 w-3.5" /> Publish
                            </Button>
                            <button
                              type="button"
                              onClick={() => deleteDraft(doc)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-rose-500/15 hover:text-rose-600"
                              title="Delete draft"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PagePanel>

      {reportFor && <SignatureDrawer doc={reportFor} onClose={() => setReportFor(null)} client={client} />}
    </PageShell>
  );
}

// ── Per-version signer report drawer ─────────────────────────────────────────
function SignatureDrawer({ doc, onClose, client }: { doc: NdaDocument; onClose: () => void; client: ApiClient }) {
  const [report, setReport] = useState<NdaSignatureReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client
      .get<NdaSignatureReport>(`/admin/nda/${doc.id}/signatures`)
      .then(setReport)
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [doc.id, client]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-background shadow-2xl">
        <div className="flex items-start justify-between border-b border-border/80 bg-card px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-foreground">
                Signatures · {doc.version_label}
              </h2>
              <p className="text-xs text-muted-foreground">Who has signed this NDA version</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <Loader className="min-h-[30vh]" />
          ) : !report ? (
            <p className="text-sm text-muted-foreground">Could not load the signature report.</p>
          ) : (
            <>
              {/* Per-role summary */}
              <div className="mb-5 flex flex-wrap gap-2">
                {Object.entries(report.signed_by_role).length === 0 ? (
                  <span className="text-sm text-muted-foreground">No signatures yet.</span>
                ) : (
                  Object.entries(report.signed_by_role).map(([role, count]) => (
                    <span
                      key={role}
                      className={`rounded-full px-3 py-1 text-xs font-bold ${ROLE_BADGE[role] ?? ROLE_BADGE.unknown}`}
                    >
                      {roleLabel(role)}: {count}
                    </span>
                  ))
                )}
              </div>

              {/* Signed */}
              <SignerSection
                title={`Signed (${report.signed.length})`}
                icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                empty="Nobody has signed this version yet."
                people={report.signed}
                showSignedAt
              />

              {/* Pending — only for the active NDA */}
              {report.is_active && (
                <div className="mt-6">
                  <SignerSection
                    title={`Pending (${report.pending.length})`}
                    icon={<Clock className="h-4 w-4 text-amber-500" />}
                    empty="Everyone has signed the active NDA. 🎉"
                    people={report.pending}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function SignerSection({
  title,
  icon,
  empty,
  people,
  showSignedAt = false,
}: {
  title: string;
  icon: React.ReactNode;
  empty: string;
  people: { user_id: string; first_name: string; last_name: string; email: string; role: string; signed_at?: string }[];
  showSignedAt?: boolean;
}) {
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-foreground">
        {icon}
        {title}
      </h3>
      {people.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {people.map((p) => (
            <li
              key={p.user_id}
              className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-white px-3 py-2.5"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">
                  {p.first_name} {p.last_name}
                </p>
                <p className="truncate text-xs text-muted-foreground">{p.email}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span
                  className={`rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider ${ROLE_BADGE[p.role] ?? ROLE_BADGE.unknown}`}
                >
                  {roleLabel(p.role)}
                </span>
                {showSignedAt && p.signed_at && (
                  <span className="text-[0.65rem] text-muted-foreground">{formatDate(p.signed_at)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
